import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useActionForOrdersMutation,
  useGetOrderQuery,
  useGetTypesOfServicesQuery,
  useSetOrderAgreementMutation,
  useUpdateOrderMutation,
} from 'redux/Api/ordersApi/ordersApi';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import {
  getDateFormattedDateForParams,
  getDateFormattedDateForParamsString,
} from 'utils/date';
import { RootState } from 'redux/store';
import { OrderStepsProgress } from '../OrderStepsProgress';
import { OrderStepsActions } from '../OrderStepsActions';
import { OrderHistory } from '../OrderHistory';
import styles from './OrderSteps.module.scss';
import { calculateStepInfo } from './constants';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Box } from '@mantine/core';
import { ModalCancelOrder } from 'components/ModalCancelOrder';
import useYandexMetrica from 'hooks/useYandexMetrica';
import { ValueOrderAgreement } from 'redux/Api/ordersApi/type';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { useGetInfoUserQuery } from 'redux/Api/profileApi/ProfileApi';

interface OrderStepsProps {
  saveOrder?: (event: React.MouseEvent<HTMLElement> | any) => void;
  saveDraft?: (event: React.MouseEvent<HTMLElement> | any) => void;
  isLoadingCreateOrder?: boolean;
  setDisableAgreement?: (disable: boolean) => void;
  disableAgreement?: boolean;
  valueOrderAgreement?: ValueOrderAgreement;
  validateConfirmVolume?: () => boolean;
  setStep?: (step: number) => void;
  disableAgreementButton?: boolean;
}

export const OrderSteps: FC<OrderStepsProps> = ({
  saveOrder,
  saveDraft,
  isLoadingCreateOrder,
  setDisableAgreement,
  disableAgreement,
  valueOrderAgreement,
  validateConfirmVolume,
  setStep,
  disableAgreementButton,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: orderData, isLoading } = useGetOrderQuery({ id }, { skip: !id });
  const { data: servicesData } = useGetTypesOfServicesQuery(undefined, { skip: !id });
  const [updateOrder, { isLoading: isUpdateLoading }] = useUpdateOrderMutation();
  const [actionForOrders, { isLoading: isActionLoading }] = useActionForOrdersMutation();
  const [isShowCancelModal, setIsShowCancelModal] = useState(false);
  const { sendGoal } = useYandexMetrica();
  const [setOrderAgreement, { isLoading: isSetOrderAgreementLoading }] =
    useSetOrderAgreementMutation();
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorInfoText, setErrorInfoText] = useState('');
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<string | null>(null);

  useEffect(() => {
    if (orderData?.order_data?.order_type && servicesData?.items) {
      const serviceType = servicesData.items.find(
        (service: any) => service.name === orderData.order_data.order_type,
      );
      if (serviceType) {
        dispatch(setInfoOrderDetail({ key: 'order_type_id', value: serviceType.id }));
      }
    }
  }, [orderData?.order_data?.order_type, servicesData?.items, dispatch]);

  const handleUpdateOrder = async (isDraft: boolean) => {
    if (!id) return;

    const {
      date_end,
      date_start,
      with_date_start,
      unit_price,
      is_remote,
      use_fast_withdraw,
      ...rest
    } = infoOrderDetail;

    await updateOrder({
      id,
      body: {
        ...rest,
        date_end:
          typeof date_end === 'object'
            ? `${getDateFormattedDateForParams(date_end)} 00:00`
            : `${getDateFormattedDateForParamsString(date_end)} 00:00`,
        date_start: with_date_start
          ? typeof date_start === 'object'
            ? `${getDateFormattedDateForParams(date_start)} 00:00`
            : `${getDateFormattedDateForParamsString(date_start)} 00:00`
          : null,
        unit_price: +unit_price,
        is_remote: is_remote === 'Удаленно',
        is_draft: isDraft,
        use_fast_withdraw: use_fast_withdraw ? 1 : 0,
      },
    });
  };

  const { refetch } = useGetInfoUserQuery();

  const handleAction = async (action: string) => {
    if (!id) return;
    try {
      setCurrentAction(action);
      await actionForOrders({ id, action }).unwrap();
    } catch (error) {
      console.error('Action failed:', error);
      throw error;
    } finally {
      setCurrentAction(null);
    }
  };

  const handleConfirmVolume = async () => {
    if (!id) return;

    if (validateConfirmVolume && !validateConfirmVolume()) {
      return;
    }
    if (disableAgreementButton) {
      return;
    }

    setDisableAgreement?.(true);

    try {
      setCurrentAction('confirm_volume');
      await setOrderAgreement({
        id,
        body: valueOrderAgreement as ValueOrderAgreement,
      }).unwrap();
      await refetch();
    } catch (error) {
      console.error('Confirm volume failed:', error);
      setDisableAgreement?.(false);
      setErrorInfoText(
        (error as { data?: { message?: string } }).data?.message ||
          'Ошибка при подтверждении объема',
      );
      setShowErrorNotification(true);
      setTimeout(() => {
        setShowErrorNotification(false);
      }, 3000);
      throw error;
    } finally {
      setCurrentAction(null);
    }
  };

  const handleEditVolume = () => {
    setDisableAgreement?.(false);
  };

  const stepInfo = calculateStepInfo(
    orderData?.status?.value,
    orderData?.receipt,
    orderData?.status,
    infoOrderDetail.is_unit_based === 1,
  );
  const {
    step,
    subtitle,
    actions: actionInfo,
    info,
    isError,
    isGray,
    showPrimaryButton,
  } = stepInfo;

  useEffect(() => {
    setStep?.(step ?? 0);
  }, [step]);

  useEffect(() => {
    if (showPrimaryButton) setDisableAgreement?.(false);
  }, [showPrimaryButton]);

  const title =
    orderData?.status?.value === 'act_signed_by_customer' &&
    orderData?.receipt?.status === 'error'
      ? orderData?.receipt?.error?.title
      : orderData?.status?.text || 'Создание заказа';

  const handleMenuAction = (actionId: string) => {
    switch (actionId) {
      case 'history':
        setIsHistoryOpen(true);
        break;
      case 'draft':
        saveDraft?.(true);
        break;
      case 'cancel':
        setIsShowCancelModal(true);
        break;
      case 'edit':
        handleEditVolume();
        break;
      case 'create_new':
        navigate('/orders/create');
        break;
    }
  };

  if (isLoading) {
    return <LoadingSkeleton fullWidth />;
  }

  const isMainButtonLoading = Boolean(
    Array.isArray(actionInfo)
      ? actionInfo.some(
          (a) =>
            (a.action === 'finish_order' && currentAction === 'accept') ||
            (a.action === 'publish' && isUpdateLoading) ||
            (a.action === 'save' && currentAction === 'save'),
        )
      : actionInfo &&
          (((actionInfo as any).action === 'finish_order' &&
            currentAction === 'accept') ||
            ((actionInfo as any).action === 'publish' && isUpdateLoading) ||
            ((actionInfo as any).action === 'save' && currentAction === 'save')),
  );

  const isButtonDisabled = Boolean(currentAction || isUpdateLoading || isActionLoading);

  return (
    <div className={styles.steps_container}>
      <div className={styles.steps}>
        <div className={styles.steps_left}>
          <p className={styles.steps_left__title}>
            {step ? `Шаг ${step} из 7. ` : null} {title}
          </p>
          <p className={styles.steps_left__subtitle}>{subtitle}</p>
          <Box visibleFrom='sm'>
            <OrderStepsProgress step={step} isError={isError} isGray={isGray} />
          </Box>
        </div>

        <OrderStepsActions
          actions={actionInfo as any}
          isUnitBased={infoOrderDetail.is_unit_based}
          info={info}
          onMenuAction={handleMenuAction}
          listItems={stepInfo.listItems}
          loading={
            isMainButtonLoading || isLoadingCreateOrder || isSetOrderAgreementLoading
          }
          disabled={isButtonDisabled}
          confirmVolume={disableAgreement}
          showPrimaryButton={stepInfo.showPrimaryButton}
          onActionClick={(clickedAction) => {
            if (!clickedAction) return;
            switch (clickedAction) {
              case 'finish_order':
                handleAction('accept');
                break;
              case 'save':
                saveOrder?.(false);
                sendGoal('CreateOrder');
                break;
              case 'publish':
                handleUpdateOrder(false);
                break;
              case 'confirm_volume':
                handleConfirmVolume();
                break;
            }
          }}
        />

        {isShowCancelModal && id && (
          <ModalCancelOrder
            isShowCancelModal={isShowCancelModal}
            setIsShowCancelModal={setIsShowCancelModal}
            orderId={id}
            onCancel={async () => {
              await handleAction('cancel');
            }}
          />
        )}

        {isHistoryOpen && (
          <OrderHistory
            history={orderData?.history}
            onClose={() => setIsHistoryOpen(false)}
          />
        )}
      </div>
      <Box hiddenFrom='sm'>
        <OrderStepsProgress step={step} isError={isError} isGray={isGray} />
      </Box>
      {showErrorNotification && (
        <SuccessErrorInfo
          type='error'
          text={errorInfoText}
          handleClose={() => setShowErrorNotification(false)}
        />
      )}
    </div>
  );
};
